/**
 * Pricing (slot model; mounted at /pricing when SLOT_MODEL is on — the old pages/Pricing.jsx
 * stays for flag-off). Audience: local businesses.
 *
 * Sept 2026 redesign: ports site/pricing/index.html of the approved package onto
 * components/site: the tinted pricing band (hero "Small format. Clear commitment.", the
 * calculator, the free-listing strip), the plan comparison ("Choose your place."), and the
 * FAQ. The calculator is components/site/pricing PricingCalculator (variant "page": plan
 * names are h2); its "Choose {Tier}" buttons open the enquiry dialog with that tier
 * pre-selected (plan context), and "Check availability" under the comparison opens it too
 * (CTA vocabulary, upgrade brief §2.2).
 *
 * Prices, slots per venue and coasters per venue (per month) come from GET /api/slots/tiers
 * (usePricingTiers), with the published list prices (lib/publishedSlotTiers) when the
 * catalogue is not published (404 / empty); the maths is lib/slotPricing's. The page loads
 * the tiers once and hands them to the calculator (its `pricing` prop), so the calculator
 * and the comparison always show the same list. On an API error no price is shown: the
 * comparison's alert announces it and offers "Try again", the calculator's box "Reload
 * prices" — the same retry, one request, and the calculator keeps the visitor's interval
 * and venue count. The design's own calculator maths (annual stacked with the
 * multi-venue discount) is not used: the discount applies to quarterly billing and Venue
 * Sponsor only (owner decision 2026-09-27).
 *
 * Structured data: a Service + OfferCatalog with one Offer per paid tier at the per-venue
 * quarterly (and, for Standard / Premium, annual) prices the comparison shows — none while
 * loading or on an error — plus the FAQPage from the FAQ list.
 */
import React from 'react';
import SEO from '../../components/SEO';
import { Eyebrow, Heading, PageHero, SiteSection } from '@/components/site';
import { EnquiryButton } from '@/components/site/enquiry';
import { PricingCalculator, annualFreeText, usePricingTiers } from '@/components/site/pricing';
import { paidTiers } from '@/components/site/pricing/pricingModel';
import { PUBLISHED_SLOT_TIERS } from '@/lib/publishedSlotTiers';
import { slotOfferCatalogJsonLd } from '../../lib/structuredData';
import { AudienceFaq, AudienceMain, BAND_TINT, PAGE_BUTTON } from './audienceParts';
import {
  ComparisonFine, FreeListingStrip, PRICING_HERO, PlanComparison, PricingBand, PricingMeasure, pricingFaq,
} from './pricingParts';

export default function PricingSlot() {
  // One request for the whole page: the calculator gets the same { tiers, status, retry }.
  const pricing = usePricingTiers();
  const { tiers, status, retry } = pricing;

  const shown = status === 'error' ? [] : paidTiers(tiers);
  // The wording that depends on the tiers ("2 months free") follows the prices on screen;
  // before they load, or when they fail, the published list prices.
  const labelTiers = shown.length ? tiers : PUBLISHED_SLOT_TIERS;
  const freeText = annualFreeText(labelTiers);
  // Offers only for prices on screen: none while loading or on an error.
  const offersJsonLd = shown.length ? slotOfferCatalogJsonLd(tiers) : null;

  return (
    <AudienceMain>
      <SEO
        title="Pricing"
        description="Compare Standard, Premium and Venue Sponsor coaster placements at local bars and restaurants: prices per venue, upfront amounts and commitments."
        url="/pricing"
        image="/images/og/pricing.jpg"
        jsonLd={offersJsonLd}
      />

      <PricingBand>
        <PageHero
          align="center"
          className={PRICING_HERO}
          eyebrow="Your place on the coasters"
          title={(
            <>
              Small format.
              {' '}
              <br />
              Clear commitment.
            </>
          )}
          lede="Choose your placement. Know exactly what’s included."
        />
        <PricingMeasure>
          <PricingCalculator variant="page" pricing={pricing} />
          <FreeListingStrip />
        </PricingMeasure>
      </PricingBand>

      <SiteSection divider className={BAND_TINT} aria-labelledby="pricing-compare-title">
        <Eyebrow>The details, side by side</Eyebrow>
        <Heading id="pricing-compare-title" className="mb-6 mt-5">Choose your place.</Heading>
        <PlanComparison tiers={tiers} status={status} onRetry={retry} />
        <ComparisonFine tiers={shown.length ? tiers : []} />
        <div className="mt-[30px] flex flex-col items-stretch gap-3.5 sm:flex-row sm:items-center">
          <EnquiryButton className={PAGE_BUTTON} role="advertiser">Check availability</EnquiryButton>
        </div>
      </SiteSection>

      <AudienceFaq
        id="pricing-faq"
        titleId="pricing-faq-title"
        title={(
          <>
            A few good
            {' '}
            <br />
            questions.
          </>
        )}
        items={pricingFaq(freeText)}
      />
    </AudienceMain>
  );
}
